import { useEffect, useRef } from 'react';
import { useI18n } from '@/context/I18nContext';

export type MaterialSubTab =
  | 'engines'
  | 'tyres'
  | 'chassis'
  | 'carburetors'
  | 'axles'
  | 'hubs'
  | 'spark-plugs';

export const MATERIAL_SUBTABS: MaterialSubTab[] = [
  'engines',
  'tyres',
  'chassis',
  'carburetors',
  'axles',
  'hubs',
  'spark-plugs',
];

const MATERIAL_SUBTAB_KEYS: Record<MaterialSubTab, string> = {
  engines: 'materials.tab.engines',
  tyres: 'materials.tab.tyres',
  chassis: 'materials.tab.chassis',
  carburetors: 'materials.tab.carburetors',
  axles: 'materials.tab.axles',
  hubs: 'materials.tab.hubs',
  'spark-plugs': 'materials.tab.spark_plugs',
};

interface Props {
  active: MaterialSubTab;
  onChange: (subtab: MaterialSubTab) => void;
}

export function MaterialSubTabBar({ active, onChange }: Props) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const btn = buttonRefs.current[active];
    const container = containerRef.current;
    if (btn && container) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [active]);

  return (
    <div
      ref={containerRef}
      className="flex gap-1 pb-0 overflow-x-auto border-b border-border-default mb-6"
      role="tablist"
      aria-label={t('nav.inventory')}
      style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
    >
      {MATERIAL_SUBTABS.map(st => (
        <button
          key={st}
          ref={el => { buttonRefs.current[st] = el; }}
          role="tab"
          aria-selected={active === st}
          onClick={() => onChange(st)}
          className={`
            bg-none border-none text-xs px-3.5 py-2 cursor-pointer shrink-0 whitespace-nowrap
            border-b-2 -mb-px rounded-t-sm
            transition-colors duration-150
            ${active === st
              ? 'text-accent border-accent font-semibold'
              : 'text-text-muted border-transparent hover:text-text-primary hover:bg-bg-hover'
            }
          `}
        >
          {t(MATERIAL_SUBTAB_KEYS[st] as any)}
        </button>
      ))}
    </div>
  );
}
